import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { successFlash, errorShake, pulse } from "../../../animations/variants";
import { useClinicStore } from "../../../store/clinicStore";
import { getProcedureByMasterId, getMasterIdByProcedureType, initProcedureRegistry } from "../../../registry/procedureRegistry";
import { emitToothEvent } from "../../../utils/toothEventBus";
import { eventBus } from "../events/eventBus";
import type { ShowToastOptions } from "../../common/ToastProvider";

import "./ProcedureSessionController.css";

const CROWN_CHART_MUTATIONS = ["RCT_CC", "RCT_CROWN", "CROWN_CUTTING", "CROWN"];

function isCrownProcedure(masterId: string | null | undefined): boolean {
  return Boolean(masterId && String(masterId).toUpperCase().includes("CROWN"));
}



interface SessionRow {
  id?: string | number;
  step_no?: number;
  total_steps?: number;
  tooth_no?: string | null;
  target_ref?: string | null;
  notes?: string;
  diagnosis?: string;
  consultant?: string;
  procedure_id?: string;
  status?: string;
  [key: string]: unknown;
}

interface ConsultantRow {
  id: string | number;
  name?: string;
  specialization?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

interface StepDef {
  id: string;
  label: string;
  chart_mutation: string | null;
}

interface ProcDefShape {
  label: string;
  steps: StepDef[];
}

interface ReplayFrame {
  symbol?: string;
  tooth?: string;
  surface?: string | null;
  multi_tooth?: string | null;
  timestamp?: string;
  [key: string]: unknown;
}

interface RegistryProcedure {
  display_name?: string;
  name?: string;
  default_steps?: (string | { label?: string; chart_mutation?: string })[];
  [key: string]: unknown;
}

export interface ProcedureSessionControllerProps {
  patientId?: string | number | null;
  visitId?: string | number | null;
  toothNo?: string | null;
  targetTeeth?: string[];
  toothStates?: Record<string, string>;
  diagnosisCode?: string | null;
  masterId?: string | null;
  initialSession?: SessionRow | null;
  resumeSession?: SessionRow | null;
  resumeHistory?: ReplayFrame[] | null;
  isLocked?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onUpdated?: (session?: SessionRow) => void | Promise<void>;
  onProcedureFinished?: () => void;
  onClearWorkflow?: () => void;
  onChartMutated?: () => void;
  onHistoryMutated?: () => void;
  consultantName?: string | null;
  autoStart?: boolean;
  showToast?: (opts: ShowToastOptions) => void;
}

export default function ProcedureSessionController({
  patientId,
  visitId,
  toothNo,
  targetTeeth,
  toothStates,
  diagnosisCode,
  masterId,
  initialSession,
  resumeSession,
  resumeHistory,
  isLocked,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onUpdated,
  onProcedureFinished,
  onClearWorkflow,
  onChartMutated,
  onHistoryMutated,
  consultantName,
  autoStart,
  showToast,
}: ProcedureSessionControllerProps) {
  const [proc, setProc] = useState<RegistryProcedure | null>(null);
  const [activeSession, setActiveSession] = useState<SessionRow | null>(resumeSession || initialSession || null);
  const activeSessionRef = useRef<SessionRow | null>(activeSession);
  useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);

  const setActiveProcedure = useClinicStore((s) => s.setActiveProcedure);

  const [notes, setNotes] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [consultant, setConsultant] = useState("");
  const [selectedSurfaces, setSelectedSurfaces] = useState<string[]>([]);

  const toggleSurface = useCallback((surface: string) => {
    setSelectedSurfaces((prev) => {
      if (prev.includes(surface)) {
        return prev.filter((s) => s !== surface);
      }
      return [...prev, surface];
    });
  }, []);

  const clearSurfaces = useCallback(() => {
    setSelectedSurfaces([]);
  }, []);

  const selectAllSurfaces = useCallback(() => {
    setSelectedSurfaces(["M", "O", "D", "B", "L"]);
  }, []);
  const [saveProgressFeedback, setSaveProgressFeedback] = useState<"idle" | "saving" | "saved">("idle");
  const [saveError, setSaveError] = useState(false);
  const [finishSuccess, setFinishSuccess] = useState(false);

  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [crownRctDone, setCrownRctDone] = useState<boolean | null>(null);
  const [abutmentPrepTeeth, setAbutmentPrepTeeth] = useState("");
  const [_consultants, setConsultants] = useState<ConsultantRow[]>([]);
  const [consultantServiceFees, setConsultantServiceFees] = useState<Array<{ consultant_id: number; consultant_name: string; service_id: number; service_name: string; fee: number; procedure_master_id?: number }>>([]);
  const [preSessionConsultant, setPreSessionConsultant] = useState("");
  const [preSessionConsultantFee, setPreSessionConsultantFee] = useState<number | null>(null);
  const [preSessionConsultantServiceId, setPreSessionConsultantServiceId] = useState<number | null>(null);

  const parseTeethInput = (raw: string): string[] =>
    raw
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);

  const selectedTeethList = useMemo(() => {
    if (targetTeeth && targetTeeth.length > 0) return targetTeeth;
    return toothNo ? [toothNo] : [];
  }, [targetTeeth, toothNo]);

  const isAbutmentSubset = useMemo(() => {
    if (!abutmentPrepTeeth.trim()) return true;
    const selected = new Set(selectedTeethList.map((t) => t.trim()));
    const requested = parseTeethInput(abutmentPrepTeeth);
    return requested.every((t) => selected.has(t));
  }, [abutmentPrepTeeth, selectedTeethList]);

  const crownStepsReady = !isCrownProcedure(masterId ?? undefined) || crownRctDone !== null;

  const mutationToState = (mutation?: string | null): string | null => {
    const m = (mutation || "").toUpperCase();
    if (!m || m === "NONE") return null;
    if (m === "CROWN" || m === "CROWN_PLACED") return "Crown";
    if (m === "CROWN_CUTTING") return "Crown_Cutting";
    if (m === "RCT_CC") return "RCT_CC";
    if (m === "RCT_CROWN") return "RCT_Crown";
    if (m === "RCT_OPEN") return "RCT_Open";
    if (m === "RCT_DONE") return "RCT_Done";
    if (m === "EXTRACT" || m === "EXTRACTION") return "Missing";
    if (m === "IMPLANT_PLACED") return "Implant";
    if (m === "IMPLANT_RESTORED") return "Crown";
    return null;
  };

  const getAffectedTeeth = () => {
    if (abutmentPrepTeeth.trim()) return parseTeethInput(abutmentPrepTeeth);
    if (targetTeeth && targetTeeth.length > 0) return targetTeeth;
    if (activeSession?.target_ref) return parseTeethInput(String(activeSession.target_ref));
    return toothNo ? [String(toothNo)] : [];
  };

  useEffect(() => {
    const loadProc = async () => {
      await initProcedureRegistry();

      if (resumeSession && !masterId) {
        const procedureType = (resumeSession as { procedure_type?: string }).procedure_type ?? "";
        const fromType = getMasterIdByProcedureType(procedureType);
        if (fromType) {
          const p = getProcedureByMasterId(fromType);
          setProc(p);
        } else if ((resumeSession as { procedure_id?: string }).procedure_id) {
          try {
            const allProcs = await apiGet("/api/doctor/procedure-registry") as Record<string, { id?: string; master_id?: string }>;
            const procEntry = Object.values(allProcs || {}).find(
              (p: { id?: string; master_id?: string }) => p.id === (resumeSession as { procedure_id?: string }).procedure_id
            );
            if (procEntry && (procEntry as { master_id?: string }).master_id) {
              const typedEntry = procEntry as { master_id: string };
              const p = getProcedureByMasterId(typedEntry.master_id);
              setProc(p);
            }
          } catch (e) {
            console.warn("Could not load procedure for resume session", e);
          }
        }
        return;
      }

      if (!masterId) return;

      const p = getProcedureByMasterId(masterId);
      setProc(p);
    };
    loadProc();
  }, [masterId, resumeSession, initialSession?.id, setActiveProcedure]);

  useEffect(() => {
    if (resumeSession) {
      setActiveSession(resumeSession);
      setActiveProcedure(resumeSession?.id != null ? String(resumeSession.id) : null);
      setNotes((resumeSession.notes as string) || "");
      setDiagnosis((resumeSession.diagnosis as string) || "");
      setConsultant((resumeSession.consultant as string) || "");

      if (resumeSession.procedure_id) {
        const loadProc = async () => {
          await initProcedureRegistry();
        };
        loadProc();
      }
    }
  }, [resumeSession, setActiveProcedure]);

  useEffect(() => {
    if (!resumeHistory?.length) return;
    // resumeHistory tracked externally via parent
  }, [resumeHistory]);

  useEffect(() => {
    if (targetTeeth && targetTeeth.length > 0) {
      setAbutmentPrepTeeth(targetTeeth.join(", "));
    } else if (toothNo) {
      setAbutmentPrepTeeth(String(toothNo));
    }
  }, [targetTeeth, toothNo]);

  useEffect(() => {
    const syncTargetRef = async () => {
      if (!activeSession?.id) return;
      if (!isCrownProcedure(masterId ?? undefined)) return;
      if (!abutmentPrepTeeth.trim()) return;
      if (!isAbutmentSubset) return;
      try {
        await apiPost(`/api/doctor/procedures/${activeSession.id}/target-ref`, {
          target_ref: abutmentPrepTeeth,
        });
        setActiveSession((prev) => (prev ? { ...prev, target_ref: abutmentPrepTeeth } : prev));
      } catch (e) {
        console.warn("Failed to sync target_ref", e);
      }
    };
    syncTargetRef();
  }, [abutmentPrepTeeth, activeSession?.id, isAbutmentSubset, masterId]);

  useEffect(() => {
    if (initialSession?.id != null) {
      setActiveSession((prev) => {
        if (prev && prev.id === initialSession.id) {
          const nextStepNo = (initialSession as SessionRow).step_no ?? prev.step_no;
          const nextTotalSteps = (initialSession as SessionRow).total_steps ?? prev.total_steps;
          if (prev.step_no === nextStepNo && prev.total_steps === nextTotalSteps) return prev;
          return { ...prev, ...(initialSession as SessionRow), step_no: nextStepNo, total_steps: nextTotalSteps };
        }
        return (initialSession as SessionRow);
      });
      setActiveProcedure(String(initialSession.id));
      setNotes((initialSession.notes as string) || "");
      setDiagnosis((initialSession.diagnosis as string) || "");
      setConsultant((initialSession.consultant as string) || "");
    }
  }, [initialSession, setActiveProcedure]);

  useEffect(() => {
    if (activeSession?.consultant) {
      setConsultant((activeSession.consultant as string) || "");
    }
  }, [activeSession?.id]);

  const loadSessions = useCallback(async () => {
    if (!visitId) return;
    try {
      const res = await apiGet(`/api/doctor/procedures/by-visit/${visitId}`);
      const rows = Array.isArray(res) ? (res as SessionRow[]) : [];
      if (!activeSessionRef.current && rows.length === 1) {
        const s = rows[0];
        setActiveSession(s);
        setActiveProcedure(s?.id != null ? String(s.id) : null);
        setNotes((s.notes as string) || "");
        setDiagnosis((s.diagnosis as string) || "");
        setConsultant((s.consultant as string) || "");
      }
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  }, [visitId, setActiveProcedure]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const loadConsultants = async () => {
      try {
        const [consultantData, feeData] = await Promise.all([
          apiGet("/api/admin/consultants"),
          apiGet("/api/manager/consultant-service-fees"),
        ]);
        setConsultants(Array.isArray(consultantData) ? (consultantData as ConsultantRow[]).filter((c: ConsultantRow) => c.is_active !== false) : []);
        setConsultantServiceFees(Array.isArray(feeData) ? feeData : []);
      } catch (e) {
        console.error("Failed to load consultants", e);
      }
    };
    loadConsultants();
  }, []);

  // Initialize consultant from prop if provided
  useEffect(() => {
    if (consultantName) {
      setPreSessionConsultant(consultantName);
      // Auto-calculate fee if possible
      const feeMapping = consultantServiceFees.find(
        f => f.consultant_name === consultantName && f.procedure_master_id === Number(masterId)
      );
      if (feeMapping) {
        setPreSessionConsultantFee(feeMapping.fee);
        setPreSessionConsultantServiceId(feeMapping.service_id);
      }
    }
  }, [consultantName, consultantServiceFees, masterId]);

  // Auto-start session if requested
  const autoStartTriggered = useRef(false);
  useEffect(() => {
    if (
      autoStart &&
      proc &&
      Array.isArray(proc.default_steps) &&
      proc.default_steps.length > 0 &&
      !activeSession &&
      !isLocked &&
      !autoStartTriggered.current
    ) {
      autoStartTriggered.current = true;
      createSession();
    }
  }, [autoStart, proc, activeSession, isLocked]);

  const procDef = useMemo((): ProcDefShape | null => {
    const totalFromSession = activeSession?.total_steps != null && Number(activeSession.total_steps) > 0
      ? Number(activeSession.total_steps)
      : Math.max(1, (activeSession?.step_no != null ? Number(activeSession.step_no) : 1));
    const labelFromSession = (activeSession as { procedure_type?: string } | undefined)?.procedure_type ?? "Procedure";

    if (!proc && activeSession && totalFromSession >= 1) {
      const steps: StepDef[] = Array.from({ length: totalFromSession }, (_, i) => ({
        id: `step_${i + 1}`,
        label: `Step ${i + 1}`,
        chart_mutation: null
      }));
      return { label: labelFromSession, steps };
    }

    if (!proc) return null;

    if (isCrownProcedure(masterId ?? undefined)) {
      const abutmentLabel = abutmentPrepTeeth
        ? `Abutment Preparation (Teeth: ${abutmentPrepTeeth})`
        : "Abutment Preparation";
      const step2Mutation = crownRctDone === true ? "RCT_CC" : crownRctDone === false ? "CROWN_CUTTING" : null;
      const step5Mutation = crownRctDone === true ? "RCT_CROWN" : crownRctDone === false ? "CROWN" : null;
      return {
        label: (proc.display_name || proc.name) as string,
        steps: [
          { id: "step_1", label: "X-ray taken", chart_mutation: null },
          { id: "step_2", label: abutmentLabel, chart_mutation: step2Mutation },
          { id: "step_3", label: "Impression taken", chart_mutation: null },
          { id: "step_4", label: "Cast taken sent to lab", chart_mutation: null },
          { id: "step_5", label: "Crown fitting Done", chart_mutation: step5Mutation }
        ]
      };
    }

    const steps = ((proc.default_steps || []) as (string | { label?: string; chart_mutation?: string })[]).map((step, i) => {
      if (typeof step === "string") {
        return { id: `step_${i + 1}`, label: step, chart_mutation: null };
      }
      return {
        id: `step_${i + 1}`,
        label: (step.label || step) as string,
        chart_mutation: step.chart_mutation || null
      };
    });

    if (steps.length === 0 && activeSession && totalFromSession > 0) {
      const syntheticSteps: StepDef[] = Array.from({ length: totalFromSession }, (_, i) => ({
        id: `step_${i + 1}`,
        label: `Step ${i + 1}`,
        chart_mutation: null
      }));
      return { label: (proc.display_name || proc.name) as string, steps: syntheticSteps };
    }

    return {
      label: (proc.display_name || proc.name) as string,
      steps
    };
  }, [proc, masterId, crownRctDone, activeSession]);

  const currentStepDef = useMemo(() => {
    if (!activeSession || !procDef?.steps?.length) return null;
    return procDef.steps[((activeSession.step_no || 1) as number) - 1] || null;
  }, [activeSession, procDef]);

  const createSession = async () => {
    if (!proc || isLocked) return;

    try {
      const stepCount = Array.isArray(proc.default_steps) ? proc.default_steps.length : 0;
      let targetRef: string | null = toothNo || null;
      if (targetTeeth && targetTeeth.length > 1) {
        targetRef = targetTeeth.join(",");
      } else if (targetTeeth && targetTeeth.length === 1) {
        targetRef = targetTeeth[0];
      }

      const res = await apiPost("/api/doctor/procedures", {
        patient_id: patientId,
        visit_id: visitId,
        procedure_master_id: masterId,
        tooth_no: toothNo || (targetTeeth && targetTeeth.length === 1 ? targetTeeth[0] : null),
        target_ref: targetRef,
        surface: null,
        surfaces: [],
        diagnosis_code: diagnosisCode || "",
        doctor_notes: "",
        procedure_type: (proc?.display_name || proc?.name || "General") as string,
        status: "IN_PROGRESS",
        current_step: 0,
        step_no: 1,
        total_steps: stepCount,
        consultant: preSessionConsultant || undefined,
        consultant_fee: preSessionConsultantFee || undefined,
        consultant_service_id: preSessionConsultantServiceId || undefined
      }) as SessionRow;

      if (!res?.id) {
        console.error("Create session failed", res);
        showToast?.({ message: "Failed to start procedure session.", type: "error" });
        return;
      }

      setActiveSession(res);
      const allSessions = await apiGet(`/api/doctor/procedures/by-visit/${visitId}`).catch(() => []);
      if (Array.isArray(allSessions)) {
        // sessions used externally via onUpdated; no local state needed
      }
      setNotes("");
      setDiagnosis(diagnosisCode || "");
      setConsultant(preSessionConsultant);
      setActiveProcedure(String(res.id));
      onUpdated?.(res);
    } catch (e) {
      console.error("Failed to start session", e);
      showToast?.({ message: "Failed to start procedure.", type: "error" });
    }
  };

  const requiresAbutmentInput = useMemo(() => {
    return isCrownProcedure(masterId ?? undefined)
      && (targetTeeth?.length || 0) > 1;
  }, [masterId, targetTeeth]);

  const isAbutmentInvalid = useMemo(() => {
    if (!requiresAbutmentInput) return false;
    return !abutmentPrepTeeth.trim() || !isAbutmentSubset;
  }, [abutmentPrepTeeth, isAbutmentSubset, requiresAbutmentInput]);

  const advanceStep = async (stepIndex: number) => {
    if (!activeSession?.id || isLocked) return;

    const steps = procDef?.steps ?? [];
    const totalSteps = steps.length || (activeSession.total_steps ?? 0);
    if (totalSteps === 0) return;

    const stepDef = steps[stepIndex - 1];
    if (isCrownProcedure(masterId ?? undefined) && crownRctDone === null) return;
    if (stepDef?.id === "step_2" && isAbutmentInvalid) return;
    // Consultant check removed - now handled at session creation
    if (stepDef?.chart_mutation === "FILLING" && selectedSurfaces.length === 0) {
      showToast?.({ message: "Select at least one surface (M/O/D/B/L).", type: "warning" });
      return;
    }
    const isLast = stepIndex >= totalSteps;
    const newStatus = isLast ? "COMPLETED" : "IN_PROGRESS";

    const surfacePayload = selectedSurfaces.length > 0 ? selectedSurfaces.join(",") : null;

    const mutationState = mutationToState(stepDef?.chart_mutation);
    if (mutationState && toothStates) {
      const teeth = getAffectedTeeth();
      if (teeth.length > 0) {
        // Only emit a single event via emitToothEvent (removes redundant eventBus.emit)
        if (stepDef?.chart_mutation && CROWN_CHART_MUTATIONS.includes(stepDef.chart_mutation) && visitId && activeSession?.tooth_no) {
          const sym = stepDef.chart_mutation;
          emitToothEvent({
            id: 0,
            encounter_id: Number(visitId),
            tooth_code: activeSession.tooth_no,
            surface: surfacePayload,
            event_type: sym.toLowerCase(),
            payload: { symbol: sym, multi_tooth: teeth.length > 1 ? teeth.join(",") : null },
            created_at: new Date().toISOString()
          });
        }
      }
    }

    try {
      const res = await apiPost(
        `/api/doctor/procedures/${activeSession.id}/step`,
        {
          step_no: stepIndex,
          step_key: `step_${stepIndex}`,
          step_label: (stepDef as { label?: string } | undefined)?.label || `Step ${stepIndex}`,
          status: newStatus,
          chart_mutation: (stepDef as { chart_mutation?: string } | undefined)?.chart_mutation ?? null,
          surface: surfacePayload,
          surfaces: selectedSurfaces,
          consultant: consultant || undefined
        }
      ) as SessionRow;

      if (stepDef?.chart_mutation === "FILLING") {
        setSelectedSurfaces([]);
      }

      if (res != null) {
        const merged = {
          ...activeSession,
          ...res,
          id: res.id ?? activeSession?.id,
          step_no: (res as { step_no?: number }).step_no ?? stepIndex,
          total_steps: (res as { total_steps?: number }).total_steps ?? activeSession?.total_steps
        };
        setActiveSession(merged as SessionRow);
        setActiveProcedure(String(merged.id ?? res.id));
      }

      if (stepDef?.chart_mutation && stepDef.chart_mutation !== "NONE" && stepDef.chart_mutation !== null) {
        // replay history tracked via eventBus listeners elsewhere
      }

      // Removed second emitToothEvent here — unified via single emission above

      onChartMutated?.();
      onHistoryMutated?.();
      if (onUpdated) onUpdated(res);

      // Auto-finish: if this was the last step, immediately call finish so the session
      // is marked COMPLETED and no longer appears in 'Resume Procedure'.
      if (newStatus === "COMPLETED" && activeSession?.id) {
        try {
          const finishRes = await apiPost(
            `/api/doctor/procedures/${activeSession.id}/finish`,
            {
              consultant: consultant || undefined,
              diagnosis: diagnosis || undefined,
              doctor_notes: notes || undefined
            }
          ) as SessionRow;
          if (finishRes?.id) {
            setActiveSession(finishRes);
          }
          setActiveProcedure(null);
          setFinishSuccess(true);
          setTimeout(() => setFinishSuccess(false), 800);
          onChartMutated?.();
          onHistoryMutated?.();
          if (onUpdated) onUpdated(finishRes);
          if (onProcedureFinished) onProcedureFinished();
        } catch (fe) {
          console.warn("Auto-finish after last step failed, showing manual Finish button", fe);
        }
      }
    } catch (e) {
      console.error("Advance step failed", e);
      showToast?.({ message: "Failed to advance step.", type: "error" });
    }
  };

  const finishSession = async () => {
    if (!activeSession?.id || isLocked) return;
    // Consultant check removed - now handled at session creation

    try {
      const res = await apiPost(
        `/api/doctor/procedures/${activeSession.id}/finish`,
        {
          consultant: consultant || undefined,
          diagnosis: diagnosis || undefined,
          doctor_notes: notes || undefined
        }
      ) as SessionRow;

      if (res?.id) {
        setActiveSession(res);
      }

      setActiveProcedure(null);
      setFinishSuccess(true);
      setTimeout(() => setFinishSuccess(false), 800);
      showToast?.({ message: "✅ Procedure completed successfully.", type: "success" });
      onChartMutated?.();
      onHistoryMutated?.();
      if (onUpdated) onUpdated(res);
      if (onProcedureFinished) onProcedureFinished();
    } catch (e) {
      console.error("Finish failed", e);
      showToast?.({ message: "Failed to finish procedure.", type: "error" });
    }
  };

  const saveClinicalNotes = async () => {
    if (!activeSession?.id || isLocked) return;
    // Consultant check removed - now handled at session creation

    setSaveProgressFeedback("saving");
    try {
      await apiPost(
        `/api/doctor/procedures/${activeSession.id}/notes`,
        {
          visit_id: visitId,
          tooth_no: toothNo,
          diagnosis,
          doctor_notes: notes,
          consultant: consultant || undefined,
          status: "In Progress",
          step_no: activeSession.step_no ?? 1,
          current_step: `step_${activeSession.step_no ?? 1}`
        }
      );

      await loadSessions();
      onChartMutated?.();
      if (onUpdated) onUpdated();
      setSaveProgressFeedback("saved");
      setSaveError(false);
      setTimeout(() => setSaveProgressFeedback("idle"), 2000);
    } catch (e) {
      console.error("Notes save failed", e);
      setSaveProgressFeedback("idle");
      setSaveError(true);
      setTimeout(() => setSaveError(false), 600);
      showToast?.({ message: "Failed to save progress.", type: "error" });
    }
  };

  const activeStepId =
    procDef && activeSession && (procDef.steps?.length ?? 0) > 0
      ? procDef.steps[(activeSession.step_no ?? 1) - 1]?.id
      : null;

  useEffect(() => {
    if (activeStepId && stepRefs.current[activeStepId]) {
      stepRefs.current[activeStepId]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeStepId]);

  if (isLocked) return null;

  if (!proc && masterId) {
    return (
      <div className="procedure-session-controller">
        <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
          <p>Loading procedure: {masterId}...</p>
        </div>
      </div>
    );
  }

  if (!proc && !activeSession) {
    return (
      <div className="procedure-session-controller">
        <div style={{ padding: "20px", textAlign: "center", color: "#ef4444" }}>
          <p>Procedure not found: {masterId}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`procedure-session-controller ${finishSuccess ? "success-flash" : ""} ${saveError ? "error-shake" : ""}`}
    >

      <div className="psc-header">
        <div className="psc-title">
          <span className="tooth-badge">{toothNo || "GEN"}</span>
          {activeSession ? (
            <strong>{procDef?.label || "Procedure"}</strong>
          ) : (
            <strong>New Procedure</strong>
          )}
        </div>
        {onClearWorkflow && (
          <button
            type="button"
            className="psc-back-button"
            onClick={() => {
              setActiveProcedure(null);
              onClearWorkflow();
            }}
            title="Back to procedure selection"
          >
            ← Back
          </button>
        )}
      </div>

      {!activeSession && (
        <div className="psc-create">
          {/* Consultant selection moved to modal in ClinicalWorkspace */}
          {preSessionConsultant && (
            <div className="consultant-info-display" style={{ marginBottom: 10, fontSize: '0.9rem', color: '#4b5563' }}>
              Performing Consultant: <strong>{preSessionConsultant}</strong>
              {preSessionConsultantFee && <span> (Fee: ₹{preSessionConsultantFee})</span>}
            </div>
          )}

          <button
            type="button"
            className="btn-start-session"
            onClick={createSession}
          >
            ▶ Start {procDef?.label || "Procedure"}
          </button>
        </div>
      )}

      {activeSession && (
        <div className="psc-active">

          {activeSession && isCrownProcedure(masterId ?? undefined) && (
            <section className="psc-section psc-section-workflow">
              <h6 className="psc-section-title">RCT already done?</h6>
              <div className="crown-selection-meta">
                Teeth: {(targetTeeth && targetTeeth.length > 0 ? targetTeeth.join(", ") : toothNo) || "—"}
              </div>
              {(targetTeeth && targetTeeth.length > 1) && (
                <div className="abutment-input-row">
                  <label>Abutment Prep Teeth</label>
                  <input
                    type="text"
                    placeholder="11, 27, 38"
                    value={abutmentPrepTeeth}
                    onChange={(e) => setAbutmentPrepTeeth(e.target.value)}
                    className={isAbutmentInvalid ? "invalid" : ""}
                  />
                  {isAbutmentInvalid && (
                    <div className="abutment-warning">Teeth must be a subset of selected teeth.</div>
                  )}
                </div>
              )}
              <div className="crown-branch-buttons">
                <button
                  type="button"
                  className={`btn-crown-branch ${crownRctDone === true ? "selected" : ""}`}
                  onClick={() => setCrownRctDone(true)}
                  disabled={activeSession !== null}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`btn-crown-branch ${crownRctDone === false ? "selected" : ""}`}
                  onClick={() => setCrownRctDone(false)}
                  disabled={activeSession !== null}
                >
                  No
                </button>
              </div>
              {activeSession !== null && crownRctDone !== null && (
                <small style={{ color: '#64748b', marginTop: 4 }}>Branch locked once session starts.</small>
              )}
            </section>
          )}

          {procDef && (procDef.steps?.length ?? 0) > 0 && (
            <section className="psc-section psc-section-workflow">
              <h6 className="psc-section-title">Treatment workflow</h6>

              <div className="step-tracker">
                {(procDef.steps ?? []).map((step, index) => {
                  const stepNum = index + 1;
                  const isActive = stepNum === (activeSession.step_no || 1);
                  const isDone = stepNum < (activeSession.step_no || 1);

                  return (
                    <div
                      key={step.id}
                      ref={(el) => {
                        stepRefs.current[step.id] = el;
                      }}
                      className={`step-node ${isActive ? "active" : ""} ${isDone ? "done" : ""} ${!crownStepsReady ? "disabled" : ""}`}
                      onClick={() => {
                        if (!crownStepsReady) return;
                        advanceStep(stepNum);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (!crownStepsReady) return;
                        if (e.key === "Enter" || e.key === " ") advanceStep(stepNum);
                      }}
                      role="button"
                      tabIndex={0}
                      title={step.label}
                    >
                      {isDone ? "✓" : stepNum}
                    </div>
                  );
                })}
              </div>

              <div className="current-step-info">
                <h5>
                  Step {activeSession.step_no || 1} of {procDef.steps?.length ?? 0}:
                  {" "}
                  {currentStepDef?.label || "No step selected"}
                  {isCrownProcedure(masterId ?? undefined) && crownRctDone !== null && (
                    <span className={`rct-badge ${crownRctDone ? "yes" : "no"}`}>
                      RCT Done: {crownRctDone ? "Yes" : "No"}
                    </span>
                  )}
                </h5>
                {!crownStepsReady && (
                  <div className="muted">Select RCT choice to enable steps.</div>
                )}
                {(canUndo || canRedo) && (
                  <div className="step-actions">
                    <button type="button" onClick={onUndo} disabled={!canUndo} className="btn-step-action btn-undo">⬅️ Undo</button>
                    <button type="button" onClick={onRedo} disabled={!canRedo} className="btn-step-action btn-redo">➡️ Redo</button>
                  </div>
                )}
              </div>
            </section>
          )}

          {(!procDef || (procDef.steps?.length ?? 0) === 0) && (
            <section className="psc-section psc-section-workflow">
              <h6 className="psc-section-title">Treatment workflow</h6>
              <div className="current-step-info">
                <h5>
                  {resumeSession ? "Resumed Session" : "Active Session"} - Step {activeSession.step_no || 1} of {activeSession.total_steps || 1}
                </h5>
              </div>
            </section>
          )}

          <div
            className={`save-progress-box ${saveProgressFeedback === "saved" ? "success-flash" : ""}`}
          >
            <button
              type="button"
              className="btn-save-progress"
              onClick={saveClinicalNotes}
              disabled={!activeSession?.id || isLocked || saveProgressFeedback === "saving"}
              title="Save current step and notes without finishing"
            >
              {saveProgressFeedback === "saving" ? "Saving…" : saveProgressFeedback === "saved" ? "✓ Saved" : "💾 Save progress"}
            </button>
            <span className="save-progress-hint">
              {saveProgressFeedback === "saved" ? "Progress saved." : "Saves current step & notes"}
            </span>
          </div>

          {procDef && (procDef.steps?.length ?? 0) > 0 && (activeSession.step_no || 0) >= (procDef.steps?.length ?? 0) && (
            <div className="finish-session-box">
              <button
                type="button"
                className="btn-finish-session"
                onClick={finishSession}
              >
                Finish Procedure
              </button>
            </div>
          )}

          {(!procDef || (procDef.steps?.length ?? 0) === 0) && (activeSession.step_no || 0) >= (activeSession.total_steps || 1) && (
            <div className="finish-session-box">
              <button
                type="button"
                className="btn-finish-session"
                onClick={finishSession}
              >
                Finish Procedure
              </button>
            </div>
          )}

          {currentStepDef?.chart_mutation === "FILLING" && (
            <div className="surface-picker-section">
              <h6 className="psc-section-title">Select Surface</h6>
              <div className="surface-picker">
                {["O", "M", "D", "B", "L"].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`surface-btn ${selectedSurfaces.includes(s) ? "active" : ""}`}
                    onClick={() => toggleSurface(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="surface-picker-actions">
                <button type="button" className="surface-action-btn" onClick={selectAllSurfaces}>All</button>
                <button type="button" className="surface-action-btn" onClick={clearSurfaces}>Clear</button>
                {selectedSurfaces.length > 0 && (
                  <span className="surface-summary">Selected: {selectedSurfaces.join(", ")}</span>
                )}
              </div>
            </div>
          )}

          {procDef && (procDef.steps?.length ?? 0) > 0 && (
            <div className="step-tools">
              <div className="tool-empty">
                <p>No specific inputs required for this step.</p>
                <button
                  type="button"
                  className="btn-next-step"
                  onClick={() => {
                    const currentStep = activeSession.step_no || 1;
                    const stepsLen = procDef?.steps?.length ?? 0;
                    // Allow advancing the current step (including the last step)
                    if (currentStep <= stepsLen) {
                      advanceStep(currentStep);
                    }
                  }}
                  disabled={!crownStepsReady || isAbutmentInvalid}
                >
                  Complete Step →
                </button>
              </div>
            </div>
          )}

          {(!procDef || (procDef.steps?.length ?? 0) === 0) && (
            <div className="step-tools">
              <div className="tool-empty">
                <p>Resumed session - use notes section to continue.</p>
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
}
